/**
 * AtlasTravelModule — SWG travel surfaces on the ground:
 *
 *   · SHUTTLEPORT (cities + towns): stand at the pad → K opens the World Atlas
 *     in TRAVEL mode; pick any city or town → `shuttleTravel` hops you there
 *     for a 10 SOV fare (server-validated boarding + fee).
 *   · SPACEPORT (cities only): stand at the landed terminal ship → Y launches
 *     (`enterSpace`) into the sector; the client then ADOPTS the space world
 *     the server moved us into (atlas:world-adopted — no enterWorld re-call).
 *
 * Port spots are deterministic (lib/ports) — the same math the settlement
 * renderer used to place the structures, well inside the server's 260 m
 * settlement-center checks.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { spaceportSpot, shuttleSpot } from '../lib/ports.js';

const PROMPT_RANGE_M = 34;

interface MapModuleLike { openTravel(): void }

export class AtlasTravelModule implements IModule {
  readonly name = 'AtlasTravel';
  private app: App | null = null;
  private prompt: HTMLDivElement | null = null;
  private timer: number | null = null;
  private sites: SettlementSite[] = [];
  private seed = '';
  private nearShuttle = false;
  private nearSpaceport = false;
  /** The planetary port (2026-09-17): the run H acts on at this spaceport, resolved each tick. */
  private portAction: { kind: 'take' | 'land'; haulId: bigint } | null = null;
  private portSubbed = false;

  init(app: App): void {
    this.app = app;
    this.prompt = document.createElement('div');
    this.prompt.style.cssText =
      'position:fixed;bottom:160px;left:50%;transform:translateX(-50%);z-index:34;display:none;' +
      'background:rgba(9,13,20,.92);border:1px solid #35506a;border-radius:8px;padding:6px 14px;' +
      'font:12px system-ui;color:#dfe9f5;box-shadow:0 4px 14px rgba(0,0,0,.5);text-align:center';
    document.body.appendChild(this.prompt);

    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.spaceFlight) return;
      if (e.code === 'KeyK' && this.nearShuttle) {
        (this.app?.getModule('AtlasWorldMap') as unknown as MapModuleLike | null)?.openTravel();
      } else if (e.code === 'KeyY' && this.nearSpaceport) {
        void this.launch();
      } else if (e.code === 'KeyL' && this.nearSpaceport) {
        // THE GROUND BRIDGE (Space Wave 2): market goods board the active hull here.
        const conn = atlas.conn;
        if (conn) {
          (conn.reducers as unknown as { loadHoldFromGround(a: Record<string, never>): Promise<void> })
            .loadHoldFromGround({})
            .then(() => bus.emit('atlas:status', { text: 'Hold loaded — your market goods are aboard' }))
            .catch((err: Error) => bus.emit('atlas:status', { text: `Load refused: ${err.message ?? err}` }));
        }
      } else if (e.code === 'KeyH' && this.nearSpaceport && this.portAction) {
        // The planetary port (2026-09-17): take an UP run here, land a DOWN run here — the crates stay the port's.
        const conn = atlas.conn;
        const act = this.portAction;
        if (conn) {
          const R = conn.reducers as unknown as { portAccept(a: { haulId: bigint }): Promise<void>; portDeliver(a: { haulId: bigint }): Promise<void> };
          (act.kind === 'take' ? R.portAccept({ haulId: act.haulId }) : R.portDeliver({ haulId: act.haulId }))
            .then(() => bus.emit('atlas:status', { text: act.kind === 'take' ? 'The port\'s crates are aboard — land them docked at the yard' : 'The run is landed — the port pays you now, the town buys as it needs' }))
            .catch((err: Error) => bus.emit('atlas:status', { text: `Port refused: ${err.message ?? err}` }));
        }
      }
    });
    this.timer = window.setInterval(() => this.tick(), 600);
  }

  private ensureSites(): void {
    const conn = atlas.conn;
    if (!conn) return;
    if (!this.portSubbed) {
      // The port rows the ground prompt reads (the sector subscribes them too; a second handle is harmless).
      this.portSubbed = true;
      try { conn.subscriptionBuilder().subscribe(['SELECT * FROM planetary_port', "SELECT * FROM port_haul WHERE state = 'offered'", "SELECT * FROM port_haul WHERE state = 'aboard'"]); } catch { /* pre-publish server */ }
    }
    for (const w of conn.db.world.iter()) {
      if (w.kind !== 'commons') continue;
      if (this.seed !== w.terrainSeed) { this.seed = w.terrainSeed; this.sites = settlementSites(this.seed); }
      return;
    }
  }

  /** The port line(s) for a city's spaceport prompt, and the run H acts on. */
  private portLines(siteId: number): string[] {
    this.portAction = null;
    const conn = atlas.conn;
    if (!conn) return [];
    interface PortRow { portId: bigint; settlementId: bigint; state: string }
    interface HaulRow { haulId: bigint; portId: bigint; direction: string; qty: number; rewardSov: bigint; state: string; hauler: { isEqual(o: unknown): boolean }; stationKey: string }
    const db = conn.db as unknown as { planetaryPort?: { iter(): Iterable<PortRow> }; portHaul?: { iter(): Iterable<HaulRow> } };
    let port: PortRow | null = null;
    try { for (const p of db.planetaryPort?.iter() ?? []) if (p.settlementId === BigInt(siteId + 1)) { port = p; break; } } catch { return []; }
    if (!port) return [];
    const me = conn.identity;
    let runs: HaulRow[] = [];
    try { runs = [...(db.portHaul?.iter() ?? [])].filter((h) => h.portId === port!.portId); } catch { runs = []; }
    const landing = me ? runs.find((h) => h.state === 'aboard' && h.direction === 'down' && h.hauler.isEqual(me)) : undefined;
    if (landing) { this.portAction = { kind: 'land', haulId: landing.haulId }; return [`<b>H</b> — Land the port run here (${landing.qty} u ore · ${landing.rewardSov} ⓒ)`]; }
    const offer = runs.find((h) => h.state === 'offered' && h.direction === 'up');
    if (offer) { this.portAction = { kind: 'take', haulId: offer.haulId }; return [`<b>H</b> — Take the port run to ${offer.stationKey} (${offer.qty} u ore · ${offer.rewardSov} ⓒ)`]; }
    const aboard = runs.find((h) => h.state === 'aboard');
    return [aboard ? `Port: a run is ${aboard.hauler.isEqual(me) ? 'in your hold — land it docked at ' + aboard.stationKey : 'aboard another pilot\'s hull'}` : `Port: ${port.state} · no run on offer`];
  }

  private tick(): void {
    if (!this.prompt) return;
    this.nearShuttle = false;
    this.nearSpaceport = false;
    if (atlas.spaceFlight || atlas.loadingGate || atlas.currentWorldId === null
      || atlas.currentWorldId !== atlas.commonsWorldId) {
      this.prompt.style.display = 'none';
      return;
    }
    this.ensureSites();
    const lines: string[] = [];
    for (const s of this.sites) {
      const dC = Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ);
      if (dC > 400) continue;
      const shut = shuttleSpot(this.seed, s);
      if (shut && Math.hypot(s.centerX + shut.x - atlas.localX, s.centerZ + shut.z - atlas.localZ) < PROMPT_RANGE_M) {
        this.nearShuttle = true;
        lines.push('<b>K</b> — Board shuttle · instant travel (10 ⓒ fare)');
      }
      const sport = spaceportSpot(this.seed, s);
      if (sport && Math.hypot(s.centerX + sport.x - atlas.localX, s.centerZ + sport.z - atlas.localZ) < PROMPT_RANGE_M + 24) {
        this.nearSpaceport = true;
        lines.push('<b>Y</b> — Launch to the sector (spaceport)');
        lines.push('<b>L</b> — Load the ship\'s hold with your market goods');
        lines.push(...this.portLines(s.id));
      }
      if (lines.length) break;
    }
    if (lines.length && !atlas.spaceFlight) { // a ground prompt has no business in the cockpit (ladder v2)
      this.prompt.innerHTML = lines.join('<br>');
      this.prompt.style.display = 'block';
    } else this.prompt.style.display = 'none';
  }

  private async launch(): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    try {
      await conn.reducers.enterSpace({});
    } catch (e) {
      bus.emit('atlas:status', { text: `Launch refused: ${(e as Error).message ?? e}` });
      return;
    }
    // The server moved our playerState into the space world — adopt it.
    let spaceId: bigint | null = null;
    for (let i = 0; i < 40 && spaceId === null; i++) {
      for (const w of conn.db.world.iter()) if (w.kind === 'space') { spaceId = w.worldId; break; }
      if (spaceId === null) await new Promise((r) => setTimeout(r, 250));
    }
    if (spaceId === null) { bus.emit('atlas:status', { text: 'The sector did not answer — try again' }); return; }
    const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    bus.emit('atlas:world-adopted', { worldId: spaceId, x: me?.posX ?? 0, z: me?.posZ ?? 0 });
  }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.prompt?.remove();
  }
}
